import { useState, useMemo, useEffect, useCallback } from "react";

const PRICE = 50;
const POOL_SIZE = 2500;
const API_URL = window.location.hostname === "localhost" 
  ? "http://localhost:8000/api" 
  : "https://tracker.lanternstreaming.com/soc-backend/public/api";

// ── Demo Data ─────────────────────────────────────────────────
const DEMO_USER = { id:1, name:"Demo Admin", email:"admin@demo.com", role:"admin" };
const DEMO_PROPERTIES = [
  { id:1, name:"Oakwood Apartments", address:"123 Oakwood Drive", contact_name:"John Smith", contact_email:"john@oakwood.com", billing_term:1,
    cameras:[
      { id:1, property_id:1, name:"Front Entrance", mac_address:"00:1A:2B:3C:4D:01", serial_number:"VTX-00412", license_key:"VTX-LIC-OAK-001", location:"Lobby", start_date:"2024-01-15", expiry_date:"2026-01-15", inv_number:"", inv_date:"", inv_years:1, inv_amount:0, pmt_date:"", pmt_amount:0, inv_sent:false, pmt_received:false, was_applied:false, applied_date:"", notes:"" },
      { id:2, property_id:1, name:"Parking Lot A", mac_address:"00:1A:2B:3C:4D:02", serial_number:"VTX-00413", license_key:"VTX-LIC-OAK-002", location:"Parking", start_date:"2024-01-15", expiry_date:"2026-01-15", inv_number:"", inv_date:"", inv_years:1, inv_amount:0, pmt_date:"", pmt_amount:0, inv_sent:false, pmt_received:false, was_applied:false, applied_date:"", notes:"PTZ" },
    ]
  },
  { id:2, name:"Riverside Plaza", address:"45 Riverside Blvd", contact_name:"Sara Lee", contact_email:"sara@riverside.com", billing_term:5,
    cameras:[
      { id:3, property_id:2, name:"Main Lobby", mac_address:"00:1A:2B:3C:4D:03", serial_number:"VTX-00510", license_key:"VTX-LIC-RVR-001", location:"Lobby", start_date:"2024-03-01", expiry_date:"2025-05-20", inv_number:"INV-1042", inv_date:"2025-03-20", inv_years:5, inv_amount:250, pmt_date:"2025-04-01", pmt_amount:250, inv_sent:true, pmt_received:true, was_applied:false, applied_date:"", notes:"" },
      { id:4, property_id:2, name:"Rear Entrance", mac_address:"00:1A:2B:3C:4D:04", serial_number:"VTX-00511", license_key:"VTX-LIC-RVR-002", location:"Rear", start_date:"2024-03-01", expiry_date:"2025-05-20", inv_number:"INV-1042", inv_date:"2025-03-20", inv_years:5, inv_amount:250, pmt_date:"", pmt_amount:0, inv_sent:true, pmt_received:false, was_applied:false, applied_date:"", notes:"" },
    ]
  },
  { id:3, name:"Summit Tower", address:"1 Summit Pkwy", contact_name:"Mike Brown", contact_email:"mike@summit.com", billing_term:5,
    cameras:[
      { id:5, property_id:3, name:"Rooftop Cam", mac_address:"00:1A:2B:3C:4D:05", serial_number:"VTX-00620", license_key:"VTX-LIC-SMT-001", location:"Roof", start_date:"2023-06-10", expiry_date:"2025-04-01", inv_number:"INV-1038", inv_date:"2025-02-28", inv_years:5, inv_amount:250, pmt_date:"2025-03-05", pmt_amount:250, inv_sent:true, pmt_received:true, was_applied:false, applied_date:"", notes:"Dome" },
      { id:6, property_id:3, name:"Floor 1 Hall", mac_address:"00:1A:2B:3C:4D:06", serial_number:"VTX-00621", license_key:"VTX-LIC-SMT-002", location:"Floor 1", start_date:"2023-06-10", expiry_date:"2025-05-15", inv_number:"INV-1038", inv_date:"2025-02-28", inv_years:5, inv_amount:250, pmt_date:"2025-03-05", pmt_amount:250, inv_sent:true, pmt_received:true, was_applied:false, applied_date:"", notes:"" },
    ]
  },
];

// ── Helpers ───────────────────────────────────────────────────
function money(n) { return "$" + parseFloat(n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,","); }
function daysLeft(exp) {
  if (!exp) return 9999;
  const n = new Date(); n.setHours(0,0,0,0);
  return Math.ceil((new Date(exp) - n) / 86400000);
}
function getStatus(d) {
  if (d < 0) return "expired";
  if (d <= 30) return "critical";
  if (d <= 60) return "warning";
  return "active";
}

// ── Global CSS ────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:      #111827;
    --bg2:     #1a2234;
    --bg3:     #141e30;
    --bg4:     #0f1623;
    --border:  #253048;
    --border2: #2e3d58;
    --text:    #e2eaf6;
    --text2:   #8fa3c8;
    --text3:   #4a5f80;
    --blue:    #3b82f6;
    --green:   #22c55e;
    --red:     #ef4444;
    --orange:  #f97316;
    --yellow:  #eab308;
    --purple:  #8b5cf6;
    --teal:    #14b8a6;
  }
  body { font-family: 'Inter', sans-serif; background: var(--bg); color: var(--text); font-size: 13px; line-height: 1.5; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: var(--bg3); }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
  input, select, textarea, button { font-family: 'Inter', sans-serif; }
  input[type=checkbox] { accent-color: var(--orange); cursor: pointer; }

  .fade-in { animation: fadeIn 0.3s ease; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }

  /* Buttons */
  .btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:none; border-radius:7px; cursor:pointer; font-size:12px; font-weight:600; transition:all 0.15s; white-space:nowrap; }
  .btn:hover { filter:brightness(1.1); }
  .btn:active { transform:scale(0.98); }
  .btn-primary { background:var(--blue); color:#fff; }
  .btn-orange  { background:var(--orange); color:#fff; }
  .btn-success { background:rgba(34,197,94,0.15); color:var(--green); border:1px solid rgba(34,197,94,0.3); }
  .btn-danger  { background:rgba(239,68,68,0.12); color:var(--red); border:1px solid rgba(239,68,68,0.25); }
  .btn-purple  { background:rgba(139,92,246,0.15); color:var(--purple); border:1px solid rgba(139,92,246,0.3); }
  .btn-ghost   { background:rgba(255,255,255,0.04); color:var(--text2); border:1px solid var(--border); }
  .btn-demo    { background:linear-gradient(135deg,rgba(249,115,22,0.15),rgba(59,130,246,0.15)); color:var(--orange); border:1px solid rgba(249,115,22,0.3); width:100%; justify-content:center; padding:11px; font-size:13px; border-radius:8px; }

  /* Inputs */
  .inp { width:100%; padding:8px 11px; background:var(--bg4); border:1px solid var(--border); border-radius:7px; color:var(--text); font-size:12px; outline:none; transition:border 0.15s; }
  .inp:focus { border-color:var(--orange); box-shadow:0 0 0 2px rgba(249,115,22,0.1); }
  .lbl { font-size:10px; color:var(--text3); font-weight:700; display:block; margin-bottom:4px; text-transform:uppercase; letter-spacing:0.7px; }

  /* Cards */
  .card { background:var(--bg2); border:1px solid var(--border); border-radius:12px; padding:18px; margin-bottom:14px; }
  .card-header { font-size:11px; font-weight:700; color:var(--text3); text-transform:uppercase; letter-spacing:1px; margin-bottom:14px; padding-left:9px; border-left:3px solid var(--orange); }

  /* Badges */
  .badge { display:inline-flex; align-items:center; gap:3px; padding:3px 9px; border-radius:6px; font-size:10px; font-weight:700; }
  .badge-active   { background:rgba(34,197,94,0.12); color:var(--green); border:1px solid rgba(34,197,94,0.25); }
  .badge-warning  { background:rgba(234,179,8,0.12); color:var(--yellow); border:1px solid rgba(234,179,8,0.25); }
  .badge-critical { background:rgba(239,68,68,0.12); color:var(--red); border:1px solid rgba(239,68,68,0.25); }
  .badge-expired  { background:rgba(74,95,128,0.15); color:var(--text3); border:1px solid var(--border); }
  .badge-blue     { background:rgba(59,130,246,0.12); color:var(--blue); border:1px solid rgba(59,130,246,0.25); }
  .badge-purple   { background:rgba(139,92,246,0.12); color:var(--purple); border:1px solid rgba(139,92,246,0.25); }
  .badge-green    { background:rgba(34,197,94,0.12); color:var(--green); border:1px solid rgba(34,197,94,0.25); }
  .badge-orange   { background:rgba(249,115,22,0.12); color:var(--orange); border:1px solid rgba(249,115,22,0.25); }

  /* Tables */
  .tbl { width:100%; border-collapse:collapse; font-size:12px; }
  .tbl th { background:var(--bg3); color:var(--text3); padding:8px 12px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.8px; font-weight:700; border-bottom:2px solid var(--border); white-space:nowrap; }
  .tbl td { padding:10px 12px; border-bottom:1px solid var(--border); vertical-align:middle; color:var(--text2); }
  .tbl td strong { color:var(--text); }
  .tbl tr:hover td { background:rgba(249,115,22,0.03); }
  .tbl tr:last-child td { border-bottom:none; }
  .tbl .mono { font-family:'JetBrains Mono',monospace; font-size:11px; }

  /* Stat cards */
  .stat-card { background:var(--bg2); border:1px solid var(--border); border-radius:11px; padding:16px 18px; transition:all 0.2s; cursor:pointer; position:relative; overflow:hidden; }
  .stat-card::after { content:''; position:absolute; bottom:0; left:0; right:0; height:2px; background:var(--orange); opacity:0; transition:opacity 0.2s; }
  .stat-card:hover { border-color:var(--border2); transform:translateY(-2px); box-shadow:0 8px 20px rgba(0,0,0,0.3); }
  .stat-card:hover::after { opacity:1; }
  .stat-card .val { font-size:28px; font-weight:800; font-family:'JetBrains Mono',monospace; line-height:1; margin-bottom:5px; }
  .stat-card .lbl { font-size:10px; color:var(--text3); text-transform:uppercase; letter-spacing:0.7px; font-weight:600; }

  /* Property cards */
  .prop-card { background:var(--bg2); border:1px solid var(--border); border-radius:12px; padding:18px; cursor:pointer; transition:all 0.2s; position:relative; }
  .prop-card:hover { border-color:var(--orange); box-shadow:0 8px 24px rgba(0,0,0,0.25); transform:translateY(-2px); }
  .prop-card .prop-stripe { position:absolute; top:0; left:0; bottom:0; width:3px; border-radius:12px 0 0 12px; }

  /* Nav */
  .nav-btn { padding:11px 18px; border:none; background:transparent; color:var(--text3); font-weight:500; font-size:13px; cursor:pointer; white-space:nowrap; border-bottom:2px solid transparent; transition:all 0.15s; }
  .nav-btn.active { color:var(--orange); border-bottom-color:var(--orange); font-weight:700; }
  .nav-btn:hover:not(.active) { color:var(--text); }

  /* Section title */
  .section-title { font-size:11px; font-weight:700; text-transform:uppercase; color:var(--text3); letter-spacing:1px; margin:20px 0 12px; display:flex; align-items:center; gap:8px; }
  .section-title::after { content:''; flex:1; height:1px; background:var(--border); }

  /* Alert boxes */
  .alert { border-radius:10px; padding:12px 16px; margin-bottom:8px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:8px; border-left:3px solid; }
  .alert-red    { background:rgba(239,68,68,0.07); border-color:var(--red); }
  .alert-yellow { background:rgba(234,179,8,0.07); border-color:var(--yellow); }
  .alert-purple { background:rgba(139,92,246,0.07); border-color:var(--purple); }
  .alert-orange { background:rgba(249,115,22,0.07); border-color:var(--orange); }

  /* Modal */
  .modal-bg { position:fixed; inset:0; background:rgba(0,0,0,0.65); display:flex; align-items:center; justify-content:center; z-index:500; padding:20px; backdrop-filter:blur(3px); }
  .modal    { background:var(--bg2); border:1px solid var(--border2); border-radius:14px; padding:26px; width:100%; max-width:460px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,0.5); animation:fadeIn 0.2s ease; }

  /* Pool bar */
  .pool-item { text-align:center; padding:0 18px; }
  .pool-item .pval { font-size:22px; font-weight:800; font-family:'JetBrains Mono',monospace; }
  .pool-item .plbl { font-size:10px; color:var(--text3); text-transform:uppercase; letter-spacing:0.6px; margin-top:3px; }
  .pdiv { width:1px; background:var(--border); align-self:stretch; min-height:36px; }

  .progress { background:var(--bg4); border-radius:4px; height:7px; overflow:hidden; }
  .progress-bar { height:100%; border-radius:4px; transition:width 0.6s; }

  .form-section { font-size:10px; font-weight:700; color:var(--orange); text-transform:uppercase; letter-spacing:1px; margin:18px 0 10px; padding-bottom:6px; border-bottom:1px solid var(--border); }
`;

function injectCSS() {
  if (!document.getElementById("vt-css")) {
    const s = document.createElement("style");
    s.id = "vt-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

// ── Status Components ─────────────────────────────────────────
function StatusBadge({ expiry }) {
  const d = daysLeft(expiry);
  const st = getStatus(d);
  const map = { active:["badge-active","✓"], warning:["badge-warning","⚠"], critical:["badge-critical","⚠"], expired:["badge-expired","✗"] };
  const lbl = d < 0 ? "Expired" : d === 0 ? "Today" : d + "d";
  const [cls, icon] = map[st];
  return <span className={`badge ${cls}`}>{icon} {lbl}</span>;
}

function Badge({ label, color="blue" }) {
  return <span className={`badge badge-${color}`}>{label}</span>;
}

// ── Login ─────────────────────────────────────────────────────
function LoginPage({ onLogin, onDemo }) {
  injectCSS();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/login`, { method:"POST", headers:{"Content-Type":"application/json","Accept":"application/json"}, body:JSON.stringify({email,password}) });
      const data = await res.json();
      if (res.ok && data.token) { localStorage.setItem("token",data.token); localStorage.setItem("user",JSON.stringify(data.user)); onLogin(data.user,data.token); }
      else setError(data.message||"Invalid credentials.");
    } catch { setError("Cannot connect to server."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ width:420 }} className="fade-in">
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <div style={{ width:44, height:44, background:"linear-gradient(135deg,var(--orange),#ea580c)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>⚡</div>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:18, fontWeight:800, color:"var(--text)", letterSpacing:"-0.3px" }}>VORTEX LICENSE TRACKER</div>
              <div style={{ fontSize:11, color:"var(--text3)", letterSpacing:"1px", textTransform:"uppercase" }}>Professional Edition</div>
            </div>
          </div>
        </div>

        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:14, padding:30, boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
          <div style={{ fontSize:16, fontWeight:700, marginBottom:20, color:"var(--text)" }}>Sign In to Your Account</div>
          {error && <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:7, padding:"9px 13px", marginBottom:16, fontSize:12, color:"var(--red)" }}>⚠ {error}</div>}
          <div style={{ marginBottom:14 }}>
            <label className="lbl">Email Address</label>
            <input className="inp" type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="admin@company.com" />
          </div>
          <div style={{ marginBottom:22 }}>
            <label className="lbl">Password</label>
            <input className="inp" type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="••••••••" />
          </div>
          <button className="btn btn-primary" onClick={handleLogin} disabled={loading} style={{ width:"100%", justifyContent:"center", padding:"10px", fontSize:13, borderRadius:8 }}>
            {loading ? "Signing In..." : "Sign In →"}
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10, margin:"16px 0" }}>
            <div style={{ flex:1, height:1, background:"var(--border)" }} />
            <span style={{ fontSize:11, color:"var(--text3)" }}>or try demo</span>
            <div style={{ flex:1, height:1, background:"var(--border)" }} />
          </div>
          <button className="btn btn-demo" onClick={onDemo}>🚀 Launch Demo — No Login Required</button>
          <p style={{ textAlign:"center", marginTop:14, fontSize:11, color:"var(--text3)" }}>Contact your administrator for account access</p>
        </div>
      </div>
    </div>
  );
}

// ── Users Page ─────────────────────────────────────────────────
function UsersPage({ token }) {
  injectCSS();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", password:"", role:"analyst" });
  const [confirmDel, setConfirmDel] = useState(null);
  const H = {"Content-Type":"application/json","Accept":"application/json","Authorization":`Bearer ${token}`};

  const load = async () => {
    setLoading(true);
    try { const r=await fetch(`${API_URL}/users`,{headers:H}); const d=await r.json(); if(r.ok) setUsers(d); else setError(d.message||"Failed"); }
    catch { setError("Cannot connect"); }
    setLoading(false);
  };
  useEffect(()=>{load();},[]);

  const add = async () => {
    if (!form.name||!form.email||!form.password) { setError("All fields required"); return; }
    setError(""); setSuccess("");
    try {
      const r=await fetch(`${API_URL}/users`,{method:"POST",headers:H,body:JSON.stringify(form)});
      const d=await r.json();
      if(r.ok){setSuccess("User added!");setShowAdd(false);setForm({name:"",email:"",password:"",role:"analyst"});load();}
      else setError(d.message||"Failed");
    } catch { setError("Cannot connect"); }
  };

  const del = async (id) => {
    try { const r=await fetch(`${API_URL}/users/${id}`,{method:"DELETE",headers:H}); if(r.ok){setSuccess("Deleted");load();}else setError("Failed"); }
    catch { setError("Cannot connect"); }
    setConfirmDel(null);
  };

  return (
    <div className="fade-in" style={{ padding:24, maxWidth:860, margin:"0 auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, letterSpacing:"-0.3px" }}>User Management</h2>
          <p style={{ fontSize:12, color:"var(--text3)", marginTop:2 }}>Admin only — manage system access</p>
        </div>
        <button className="btn btn-primary" onClick={()=>{setShowAdd(!showAdd);setError("");setSuccess("");}}>
          {showAdd ? "Cancel" : "+ Add User"}
        </button>
      </div>
      {error && <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:7, padding:"9px 13px", marginBottom:14, fontSize:12, color:"var(--red)" }}>{error}</div>}
      {success && <div style={{ background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:7, padding:"9px 13px", marginBottom:14, fontSize:12, color:"var(--green)" }}>{success}</div>}
      {showAdd && (
        <div className="card" style={{ marginBottom:20 }}>
          <div className="card-header">New User</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            {[["name","Full Name","text"],["email","Email","email"],["password","Password","password"]].map(([k,l,t])=>(
              <div key={k}><label className="lbl">{l}</label><input className="inp" type={t} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} /></div>
            ))}
            <div><label className="lbl">Role</label>
              <select className="inp" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                <option value="analyst">🔍 Analyst</option>
                <option value="admin">👑 Admin</option>
              </select>
            </div>
          </div>
          <button className="btn btn-primary" onClick={add}>Save User</button>
        </div>
      )}
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
        <table className="tbl">
          <thead><tr>{["Name","Email","Role","Created",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ textAlign:"center", color:"var(--text3)", padding:30 }}>Loading...</td></tr>}
            {!loading&&users.length===0 && <tr><td colSpan={5} style={{ textAlign:"center", color:"var(--text3)", padding:30 }}>No users found.</td></tr>}
            {users.map(u=>(
              <tr key={u.id}>
                <td><strong>{u.name}</strong></td>
                <td className="mono">{u.email}</td>
                <td><Badge label={u.role==="admin"?"👑 Admin":"🔍 Analyst"} color={u.role==="admin"?"purple":"blue"} /></td>
                <td>{u.created_at?new Date(u.created_at).toLocaleDateString():"—"}</td>
                <td><button className="btn btn-danger" style={{ padding:"4px 10px",fontSize:11 }} onClick={()=>setConfirmDel(u)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmDel && (
        <div className="modal-bg">
          <div className="modal">
            <div style={{ fontSize:16, fontWeight:700, color:"var(--red)", marginBottom:10 }}>Remove User?</div>
            <p style={{ color:"var(--text2)", fontSize:13, marginBottom:20 }}>Remove <strong>{confirmDel.name}</strong> from the system?</p>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button className="btn btn-ghost" onClick={()=>setConfirmDel(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={()=>del(confirmDel.id)}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────
function Dashboard({ properties, onNavigate }) {
  injectCSS();
  const allCams = properties.flatMap(p=>(p.cameras||[]).map(c=>({...c,propName:p.name,propId:p.id})));
  const pool = { total:POOL_SIZE, assigned:allCams.length, avail:POOL_SIZE-allCams.length };
  const pct = Math.round((pool.assigned/pool.total)*100);
  const s = {
    active:     allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="active").length,
    warning:    allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="warning").length,
    critical:   allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="critical").length,
    expired:    allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="expired").length,
    invDue:     allCams.filter(c=>{ const d=daysLeft(c.expiry_date); return d>=0&&d<=60&&!c.inv_sent; }).length,
    awaitPmt:   allCams.filter(c=>c.inv_sent&&!c.pmt_received).length,
    readyApply: allCams.filter(c=>c.pmt_received&&!c.was_applied).length,
  };
  const actionItems = [
    ...allCams.filter(c=>c.pmt_received&&!c.was_applied).map(c=>({type:"apply",c})),
    ...allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="expired").map(c=>({type:"expired",c})),
    ...allCams.filter(c=>getStatus(daysLeft(c.expiry_date))==="critical").map(c=>({type:"critical",c})),
  ].slice(0,6);
  const propStripes=["#7c6fe0","#2dd4bf","#a78bfa","#fbbf24","#34d399"];

  useEffect(()=>{
    const loadAndDraw = () => {
      if(!window.Chart){
        if(!document.getElementById("vt-chartjs")){
          const sc=document.createElement("script");
          sc.id="vt-chartjs";
          sc.src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
          sc.onload=()=>drawCharts();
          document.head.appendChild(sc);
        } else {
          setTimeout(loadAndDraw,300);
        }
      } else {
        drawCharts();
      }
    };
    const drawCharts = () => {
      ["vt-donut","vt-revbar"].forEach(id=>{
        const el=document.getElementById(id);
        if(el&&el._vtc){el._vtc.destroy();delete el._vtc;}
      });
      const tc="#8fa3c8";
      const gc="rgba(255,255,255,0.07)";
      const bg="#1a2234";
      const de=document.getElementById("vt-donut");
      if(de && window.Chart) {
        de._vtc=new window.Chart(de,{
          type:"doughnut",
          data:{
            labels:["Active","Expiring","Critical","Expired"],
            datasets:[{
              data:[s.active||0.001,s.warning||0.001,s.critical||0.001,s.expired||0.001],
              backgroundColor:["#22c55e","#f59e0b","#ef4444","#6b7280"],
              borderWidth:3,
              borderColor:bg,
              hoverOffset:4
            }]
          },
          options:{responsive:true,maintainAspectRatio:false,cutout:"72%",plugins:{legend:{display:false},tooltip:{callbacks:{label:c=>" "+(c.raw<1?0:Math.round(c.raw))+" cameras"}}}}
        });
      }
      const re=document.getElementById("vt-revbar");
      const barColors=["#7c6fe0","#2dd4bf","#a78bfa","#fbbf24","#34d399"];
      if(re && window.Chart) {
        re._vtc=new window.Chart(re,{
          type:"bar",
          data:{
            labels:properties.map(p=>p.name.split(" ")[0]),
            datasets:[{
              data:properties.map(p=>(p.cameras||[]).length*PRICE),
              backgroundColor:properties.map((_,i)=>barColors[i%barColors.length]),
              borderRadius:6,
              borderSkipped:false,
              maxBarThickness:48,
            }]
          },
          options:{
            responsive:true,
            maintainAspectRatio:false,
            plugins:{legend:{display:false},tooltip:{callbacks:{label:c=>" $"+c.raw+"/yr"}}},
            scales:{
              x:{grid:{display:false},ticks:{color:tc,font:{size:11}}},
              y:{grid:{color:gc},ticks:{color:tc,font:{size:11},callback:v=>"$"+v},beginAtZero:true,suggestedMax:150}
            }
          }
        });
      }
    };
    loadAndDraw();
  },[properties]);

  return (
    <div className="fade-in" style={{padding:"16px 20px"}}>

      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:12}}>
        {[
          {l:"Available Licenses",v:pool.avail.toLocaleString(),s:"of "+pool.total.toLocaleString()+" · "+pct+"% used",g:"linear-gradient(135deg,#4c3f8f,#7c6fe0)"},
          {l:"Cameras Assigned",v:pool.assigned,s:properties.length+" properties",g:"linear-gradient(135deg,#0e6b6b,#2dd4bf)"},
          {l:"Annual Revenue",v:money(pool.assigned*PRICE),s:money(PRICE)+"/cam/yr",g:"linear-gradient(135deg,#1a4731,#34d399)"},
          {l:"5-Year Revenue",v:money(pool.assigned*PRICE*5),s:"projected total",g:"linear-gradient(135deg,#5c2d8a,#a78bfa)"},
        ].map(k=>(
          <div key={k.l} style={{background:k.g,borderRadius:12,padding:"14px 16px",color:"#fff"}}>
            <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".7px",opacity:.8,marginBottom:6,fontWeight:600}}>{k.l}</div>
            <div style={{fontSize:26,fontWeight:700,lineHeight:1,marginBottom:4}}>{k.v}</div>
            <div style={{fontSize:11,opacity:.7}}>{k.s}</div>
          </div>
        ))}
      </div>

      {/* Charts + Actions */}
      <div style={{display:"grid",gridTemplateColumns:"200px 1fr 210px",gap:10,marginBottom:12}}>

        {/* Donut */}
        <div className="card" style={{padding:14}}>
          <div className="card-title">License Status</div>
          <div style={{position:"relative",height:140}}>
            <canvas id="vt-donut" role="img" aria-label="License status donut chart">6 expired.</canvas>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,marginTop:10}}>
            {[["#22c55e","Active",s.active],["#f59e0b","Expiring",s.warning],["#ef4444","Critical",s.critical],["#6b7280","Expired",s.expired]].map(([c,l,v])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:8,height:8,borderRadius:2,background:c,flexShrink:0}}/>
                <span style={{fontSize:11,color:"var(--text3)"}}>{l}: <strong style={{color:"var(--text)"}}>{v}</strong></span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar chart */}
        <div className="card" style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div className="card-title" style={{margin:0}}>Annual Revenue by Property</div>
            <div style={{display:"flex",gap:10}}>
              {properties.map((p,i)=>(
                <span key={p.id} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--text3)"}}>
                  <span style={{width:8,height:8,borderRadius:2,background:propStripes[i%propStripes.length],flexShrink:0}}/>
                  {p.name.split(" ")[0]}
                </span>
              ))}
            </div>
          </div>
          <div style={{position:"relative",height:155}}>
            <canvas id="vt-revbar" role="img" aria-label="Revenue by property bar chart">Revenue chart.</canvas>
          </div>
        </div>

        {/* Action required */}
        <div className="card" style={{padding:14}}>
          <div className="card-title" style={{color:"#ef4444",marginBottom:10}}>⚡ Action Required</div>
          {actionItems.length===0
            ?<div style={{textAlign:"center",padding:"16px 0",color:"var(--text3)",fontSize:12}}>✓ All clear!</div>
            :actionItems.map(({type,c},i)=>{
              const isApply=type==="apply",isExp=type==="expired";
              const color=isApply?"#a78bfa":isExp?"#ef4444":"#f59e0b";
              const label=isApply?"Apply":isExp?"Expired":"Critical";
              return (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:7,padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0,marginTop:4}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:600,color}}>{label}</div>
                    <div style={{fontSize:10,color:"var(--text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.propName} / {c.name}</div>
                  </div>
                </div>
              );
            })
          }
          {actionItems.length>0&&<button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",marginTop:8,fontSize:11}} onClick={()=>onNavigate("apply")}>View Apply Tab →</button>}
        </div>
      </div>

      {/* Stats - compact single row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:12}}>
        {[
          {l:"Active",v:s.active,c:"#22c55e",page:null},
          {l:"Expiring",v:s.warning,c:"#f59e0b",page:"cameras"},
          {l:"Critical",v:s.critical,c:"#ef4444",page:"cameras"},
          {l:"Expired",v:s.expired,c:"#6b7280",page:"cameras"},
          {l:"Invoice Due",v:s.invDue,c:"#60a5fa",page:"invoices"},
          {l:"Ready Apply",v:s.readyApply,c:"#a78bfa",page:"apply"},
        ].map(sc=>(
          <div key={sc.l} onClick={()=>sc.page&&onNavigate(sc.page)}
            style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px",cursor:sc.page?"pointer":"default",borderBottom:"2px solid "+sc.c,transition:"opacity .15s"}}
            onMouseEnter={e=>{if(sc.page)e.currentTarget.style.opacity=".8";}}
            onMouseLeave={e=>{e.currentTarget.style.opacity="1";}}>
            <div style={{fontSize:22,fontWeight:700,color:sc.c,lineHeight:1,marginBottom:3}}>{sc.v}</div>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".5px"}}>{sc.l}</div>
          </div>
        ))}
      </div>

      {/* Properties */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:13,fontWeight:600}}>Properties <span style={{fontSize:11,color:"var(--text3)",fontWeight:400}}>{properties.length} total · {allCams.length} cameras</span></span>
        <button className="btn btn-orange" onClick={()=>onNavigate("addproperty")}>+ Add Property</button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:10}}>
        {properties.map((p,idx)=>{
          const pc=p.cameras||[];
          const sts=pc.map(c=>getStatus(daysLeft(c.expiry_date)));
          const worst=sts.includes("expired")?"expired":sts.includes("critical")?"critical":sts.includes("warning")?"warning":"active";
          const wc={active:"#22c55e",warning:"#f59e0b",critical:"#ef4444",expired:"#6b7280"}[worst];
          const wl={active:"Healthy",warning:"Expiring",critical:"Critical",expired:"Expired"}[worst];
          const invDue=pc.filter(c=>{const d=daysLeft(c.expiry_date);return d>=0&&d<=60&&!c.inv_sent;}).length;
          const awaitPmt=pc.filter(c=>c.inv_sent&&!c.pmt_received).length;
          const readyApp=pc.filter(c=>c.pmt_received&&!c.was_applied).length;
          const near=pc.length?Math.min(...pc.map(c=>daysLeft(c.expiry_date))):null;
          const activeCount=pc.filter(c=>getStatus(daysLeft(c.expiry_date))==="active").length;
          const sc=propStripes[idx%propStripes.length];
          return (
            <div key={p.id} onClick={()=>onNavigate("property",p.id)}
              style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:12,overflow:"hidden",cursor:"pointer",transition:"all .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=sc;e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.3)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
              <div style={{height:3,background:sc}}/>
              <div style={{padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{p.name}</div>
                  <span style={{background:wc+"22",color:wc,border:"1px solid "+wc+"44",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700,flexShrink:0,marginLeft:8}}>{wl}</span>
                </div>
                <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>{p.address||"—"}{p.contact_name&&" · "+p.contact_name}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",textAlign:"center",padding:"8px 0",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)",marginBottom:8}}>
                  {[
                    [pc.length,"#60a5fa","Cameras"],
                    [money(pc.length*PRICE),"#22c55e","Annual"],
                    [(p.billing_term||1)+"yr","var(--text2)","Billing"],
                    [near===null?"—":near<0?"Exp":near+"d",near===null||near<0?"#6b7280":near<=30?"#ef4444":near<=60?"#f59e0b":"#22c55e","Next Exp"],
                  ].map(([v,c,l])=>(
                    <div key={l}>
                      <div style={{fontSize:14,fontWeight:700,color:c}}>{v}</div>
                      <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",marginTop:1}}>{l}</div>
                    </div>
                  ))}
                </div>
                {pc.length>0&&(
                  <div style={{height:4,borderRadius:2,overflow:"hidden",display:"flex",gap:1,marginBottom:7}}>
                    {activeCount>0&&<div style={{flex:activeCount,background:"#22c55e",opacity:.7}}/>}
                    {(pc.length-activeCount)>0&&<div style={{flex:pc.length-activeCount,background:wc,opacity:.6}}/>}
                  </div>
                )}
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {readyApp>0&&<span style={{background:"rgba(167,139,250,.15)",color:"#a78bfa",border:"1px solid rgba(167,139,250,.3)",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600}}>{readyApp} apply</span>}
                  {awaitPmt>0&&<span style={{background:"rgba(245,158,11,.15)",color:"#f59e0b",border:"1px solid rgba(245,158,11,.3)",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600}}>{awaitPmt} unpaid</span>}
                  {invDue>0&&<span style={{background:"rgba(96,165,250,.15)",color:"#60a5fa",border:"1px solid rgba(96,165,250,.3)",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:600}}>{invDue} invoice</span>}
                  {(pc.length-activeCount)>0&&readyApp===0&&awaitPmt===0&&invDue===0&&<span style={{fontSize:10,color:wc}}>{pc.length-activeCount} need attention</span>}
                </div>
              </div>
            </div>
          );
        })}
        {properties.length===0&&(
          <div style={{gridColumn:"1/-1",textAlign:"center",padding:50,color:"var(--text3)",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:12}}>
            <div style={{fontSize:36,marginBottom:10}}>🏢</div>
            <div style={{fontWeight:600,color:"var(--text)",marginBottom:6}}>No properties yet</div>
            <button className="btn btn-orange" style={{marginTop:8}} onClick={()=>onNavigate("addproperty")}>+ Add Property</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Property Form ─────────────────────────────────────────────
function PropertyForm({ property, onSave, onCancel }) {
  injectCSS();
  const [form, setForm] = useState({ name:property?.name||"", address:property?.address||"", contact_name:property?.contact_name||"", contact_email:property?.contact_email||"", billing_term:property?.billing_term||1 });
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  return (
    <div className="fade-in" style={{ padding:24, maxWidth:560, margin:"0 auto" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22 }}>
        <button className="btn btn-ghost" onClick={onCancel}>← Back</button>
        <h2 style={{ fontSize:18, fontWeight:800 }}>{property?"Edit Property":"New Property"}</h2>
      </div>
      <div className="card">
        <div className="card-header">{property?"Edit Details":"Property Information"}</div>
        <div style={{ marginBottom:13 }}><label className="lbl">Property Name *</label><input className="inp" value={form.name} onChange={e=>f("name",e.target.value)} placeholder="Oakwood Apartments" /></div>
        <div style={{ marginBottom:13 }}><label className="lbl">Address</label><input className="inp" value={form.address} onChange={e=>f("address",e.target.value)} placeholder="123 Main St, City, State" /></div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:13 }}>
          <div><label className="lbl">Contact Name</label><input className="inp" value={form.contact_name} onChange={e=>f("contact_name",e.target.value)} placeholder="John Smith" /></div>
          <div><label className="lbl">Contact Email</label><input className="inp" type="email" value={form.contact_email} onChange={e=>f("contact_email",e.target.value)} placeholder="john@example.com" /></div>
        </div>
        <div style={{ marginBottom:20 }}><label className="lbl">Billing Term</label>
          <select className="inp" value={form.billing_term} onChange={e=>f("billing_term",parseInt(e.target.value))}>
            <option value={1}>1 Year — {money(PRICE)} per camera/year</option>
            <option value={5}>5 Years — {money(PRICE*5)} per camera total</option>
          </select>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn btn-orange" onClick={()=>onSave(form)}>Save Property</button>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Camera Form ───────────────────────────────────────────────
function CameraForm({ camera, properties, defaultPropId, onSave, onCancel }) {
  injectCSS();
  const [form, setForm] = useState({
    property_id:camera?.property_id||defaultPropId||"", name:camera?.name||"", mac_address:camera?.mac_address||"",
    serial_number:camera?.serial_number||"", license_key:camera?.license_key||"", location:camera?.location||"",
    start_date:camera?.start_date||new Date().toISOString().split("T")[0], expiry_date:camera?.expiry_date||"",
    inv_number:camera?.inv_number||"", inv_date:camera?.inv_date||"", inv_years:camera?.inv_years||1, inv_amount:camera?.inv_amount||0,
    pmt_date:camera?.pmt_date||"", pmt_amount:camera?.pmt_amount||0, inv_sent:camera?.inv_sent||false,
    pmt_received:camera?.pmt_received||false, was_applied:camera?.was_applied||false, applied_date:camera?.applied_date||"", notes:camera?.notes||"",
  });
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  return (
    <div className="fade-in" style={{ padding:24, maxWidth:660, margin:"0 auto" }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22 }}>
        <button className="btn btn-ghost" onClick={onCancel}>← Back</button>
        <h2 style={{ fontSize:18, fontWeight:800 }}>{camera?"Edit Camera":"Add Camera"}</h2>
      </div>
      <div className="card">
        <div className="form-section">Camera Details</div>
        <div style={{ marginBottom:12 }}><label className="lbl">Property *</label>
          <select className="inp" value={form.property_id} onChange={e=>f("property_id",e.target.value)}>
            <option value="">Select property...</option>
            {properties.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom:12 }}><label className="lbl">Camera Name *</label><input className="inp" value={form.name} onChange={e=>f("name",e.target.value)} placeholder="Front Entrance" /></div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div><label className="lbl">MAC Address</label><input className="inp mono" value={form.mac_address} onChange={e=>f("mac_address",e.target.value)} placeholder="00:1A:2B:3C:4D:5E" /></div>
          <div><label className="lbl">Serial Number</label><input className="inp mono" value={form.serial_number} onChange={e=>f("serial_number",e.target.value)} placeholder="VTX-00000" /></div>
        </div>
        <div style={{ marginBottom:12 }}><label className="lbl">License Key *</label><input className="inp mono" value={form.license_key} onChange={e=>f("license_key",e.target.value)} placeholder="VTX-LIC-XXX-001" /></div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div><label className="lbl">Location</label><input className="inp" value={form.location} onChange={e=>f("location",e.target.value)} placeholder="Lobby" /></div>
          <div><label className="lbl">License Start</label><input className="inp" type="date" value={form.start_date} onChange={e=>f("start_date",e.target.value)} /></div>
        </div>
        <div style={{ marginBottom:12 }}><label className="lbl">Expiry Date *</label><input className="inp" type="date" value={form.expiry_date} onChange={e=>f("expiry_date",e.target.value)} /></div>

        <div className="form-section">Invoice Details</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div><label className="lbl">Invoice #</label><input className="inp" value={form.inv_number} onChange={e=>f("inv_number",e.target.value)} placeholder="INV-1001" /></div>
          <div><label className="lbl">Invoice Date</label><input className="inp" type="date" value={form.inv_date} onChange={e=>f("inv_date",e.target.value)} /></div>
          <div><label className="lbl">Invoice Years</label>
            <select className="inp" value={form.inv_years} onChange={e=>f("inv_years",parseInt(e.target.value))}>
              <option value={1}>1 Year</option><option value={5}>5 Years</option>
            </select>
          </div>
          <div><label className="lbl">Invoice Amount</label><input className="inp" type="number" value={form.inv_amount} onChange={e=>f("inv_amount",parseFloat(e.target.value)||0)} /></div>
          <div><label className="lbl">Invoice Sent?</label>
            <select className="inp" value={form.inv_sent?"yes":"no"} onChange={e=>f("inv_sent",e.target.value==="yes")}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </div>
        </div>

        <div className="form-section">Payment Details</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div><label className="lbl">Payment Date</label><input className="inp" type="date" value={form.pmt_date} onChange={e=>f("pmt_date",e.target.value)} /></div>
          <div><label className="lbl">Payment Amount</label><input className="inp" type="number" value={form.pmt_amount} onChange={e=>f("pmt_amount",parseFloat(e.target.value)||0)} /></div>
          <div><label className="lbl">Payment Received?</label>
            <select className="inp" value={form.pmt_received?"yes":"no"} onChange={e=>f("pmt_received",e.target.value==="yes")}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </div>
        </div>

        <div className="form-section">Application Status</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div><label className="lbl">Applied in Vortex?</label>
            <select className="inp" value={form.was_applied?"yes":"no"} onChange={e=>f("was_applied",e.target.value==="yes")}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </div>
          <div><label className="lbl">Applied Date</label><input className="inp" type="date" value={form.applied_date} onChange={e=>f("applied_date",e.target.value)} /></div>
        </div>
        <div style={{ marginBottom:20 }}><label className="lbl">Notes</label><textarea className="inp" value={form.notes} onChange={e=>f("notes",e.target.value)} placeholder="Camera type, coverage area..." rows={2} style={{ resize:"vertical" }} /></div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn btn-orange" onClick={()=>onSave(form)}>Save Camera</button>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Property Detail ───────────────────────────────────────────
function PropertyDetail({ property, onBack, onEditProp, onAddCamera, onEditCamera, onDeleteCamera, onDeleteProp }) {
  injectCSS();
  const cameras = property.cameras||[];
  const exportCSV = () => {
    const keys = ["name","mac_address","serial_number","license_key","location","start_date","expiry_date","inv_number","inv_date","inv_years","inv_amount","pmt_date","pmt_amount","inv_sent","pmt_received","was_applied","applied_date","notes"];
    const labels = ["Camera","MAC","Serial","License Key","Location","Start","Expiry","Inv#","Inv Date","Inv Yrs","Inv Amt","Pmt Date","Pmt Amt","Inv Sent","Pmt Rcvd","Applied","Applied Date","Notes"];
    const csv = labels.join(",") + "\n" + cameras.map(c=>keys.map(k=>`"${(c[k]||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`${property.name.replace(/\s+/g,"_")}.csv`; a.click();
  };
  return (
    <div className="fade-in" style={{ padding:24 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:14, marginBottom:20, flexWrap:"wrap" }}>
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <div style={{ flex:1 }}>
          <h2 style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>{property.name}</h2>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:12, color:"var(--text3)" }}>
            {property.address&&<span>📍 {property.address}</span>}
            {property.contact_name&&<span>👤 {property.contact_name}</span>}
            {property.contact_email&&<span>✉ {property.contact_email}</span>}
            <span>📋 {property.billing_term||1}-year billing · <span style={{ color:"var(--green)" }}>{money(cameras.length*PRICE)}/yr</span></span>
          </div>
        </div>
        <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
          <button className="btn btn-success" onClick={exportCSV}>⬇ Export CSV</button>
          <button className="btn btn-orange" onClick={onAddCamera}>+ Add Camera</button>
          <button className="btn btn-ghost" onClick={onEditProp}>Edit</button>
          <button className="btn btn-danger" onClick={onDeleteProp}>Delete</button>
        </div>
      </div>
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table className="tbl">
            <thead><tr>{["Camera","MAC Address","License Key","Expiry","Status","Invoice","Payment","Applied",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {cameras.length===0&&<tr><td colSpan={9} style={{ textAlign:"center", color:"var(--text3)", padding:40 }}>No cameras. Click + Add Camera.</td></tr>}
              {cameras.map(c=>{
                const d=daysLeft(c.expiry_date);
                const invDue=d>=0&&d<=60&&!c.inv_sent, awaitPmt=c.inv_sent&&!c.pmt_received, readyApply=c.pmt_received&&!c.was_applied;
                const bg=readyApply?"rgba(139,92,246,0.05)":awaitPmt?"rgba(234,179,8,0.04)":invDue?"rgba(59,130,246,0.04)":"";
                return (
                  <tr key={c.id} style={{ background:bg }}>
                    <td><strong>{c.name}</strong>{c.notes&&<div style={{ fontSize:10, color:"var(--text3)" }}>{c.notes}</div>}</td>
                    <td className="mono">{c.mac_address||"—"}</td>
                    <td className="mono">{c.license_key||"—"}</td>
                    <td className="mono">{c.expiry_date||"—"}</td>
                    <td><StatusBadge expiry={c.expiry_date} /></td>
                    <td>{c.inv_sent?<><Badge label={c.inv_number||"Sent"} color="green"/><div style={{ fontSize:10, color:"var(--text3)" }}>{c.inv_date} · {money(c.inv_amount)}</div></>:invDue?<Badge label="Due" color="blue"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>{c.pmt_received?<><Badge label="Paid" color="green"/><div style={{ fontSize:10, color:"var(--text3)" }}>{c.pmt_date} · {money(c.pmt_amount)}</div></>:awaitPmt?<Badge label="Awaiting" color="warning"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>{c.was_applied?<Badge label={"✓ "+c.applied_date} color="green"/>:readyApply?<Badge label="Ready" color="purple"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding:"4px 9px",fontSize:11,marginRight:4 }} onClick={()=>onEditCamera(c)}>Edit</button>
                      <button className="btn btn-danger" style={{ padding:"4px 9px",fontSize:11 }} onClick={()=>onDeleteCamera(c.id)}>Del</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── All Cameras ───────────────────────────────────────────────
function AllCameras({ properties, onAddCamera, onEditCamera, onDeleteCamera }) {
  injectCSS();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const allCams = properties.flatMap(p=>(p.cameras||[]).map(c=>({...c,propName:p.name,propId:p.id})));
  const filtered = useMemo(()=>{
    let r=allCams;
    if(filter==="warning")  r=r.filter(c=>getStatus(daysLeft(c.expiry_date))==="warning");
    else if(filter==="critical") r=r.filter(c=>getStatus(daysLeft(c.expiry_date))==="critical");
    else if(filter==="expired")  r=r.filter(c=>getStatus(daysLeft(c.expiry_date))==="expired");
    else if(filter==="invoice")  r=r.filter(c=>{ const d=daysLeft(c.expiry_date); return d>=0&&d<=60&&!c.inv_sent; });
    else if(filter==="payment")  r=r.filter(c=>c.inv_sent&&!c.pmt_received);
    else if(filter==="apply")    r=r.filter(c=>c.pmt_received&&!c.was_applied);
    if(search){ const q=search.toLowerCase(); r=r.filter(c=>[c.name,c.propName,c.license_key,c.mac_address,c.serial_number,c.inv_number].some(v=>(v||"").toLowerCase().includes(q))); }
    return r.sort((a,b)=>daysLeft(a.expiry_date)-daysLeft(b.expiry_date));
  },[allCams,search,filter]);

  const exportCSV = () => {
    const keys=["propName","name","mac_address","serial_number","license_key","location","start_date","expiry_date","inv_number","inv_date","inv_years","inv_amount","pmt_date","pmt_amount","inv_sent","pmt_received","was_applied","applied_date","notes"];
    const labels=["Property","Camera","MAC","Serial","License Key","Location","Start","Expiry","Inv#","Inv Date","Inv Yrs","Inv Amt","Pmt Date","Pmt Amt","Inv Sent","Pmt Rcvd","Applied","Applied Date","Notes"];
    const csv=labels.join(",")+"\n"+filtered.map(c=>keys.map(k=>`"${(c[k]||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="vortex-cameras.csv"; a.click();
  };

  return (
    <div className="fade-in" style={{ padding:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:10 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800 }}>All Cameras</h2>
          <p style={{ fontSize:12, color:"var(--text3)", marginTop:2 }}>{filtered.length} of {allCams.length} cameras shown</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn btn-success" onClick={exportCSV}>⬇ Export CSV</button>
          <button className="btn btn-orange" onClick={onAddCamera}>+ Add Camera</button>
        </div>
      </div>
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <input className="inp" placeholder="Search by camera, property, MAC, serial, license key..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1, minWidth:220 }} />
        <select className="inp" value={filter} onChange={e=>setFilter(e.target.value)} style={{ width:"auto", minWidth:180 }}>
          {[["all","All Cameras"],["warning","Expiring (31-60d)"],["critical","Critical (≤30d)"],["expired","Expired"],["invoice","Invoice Due"],["payment","Awaiting Payment"],["apply","Ready to Apply"]].map(([v,l])=>(
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table className="tbl">
            <thead><tr>{["Property","Camera","MAC","License Key","Expiry","Status","Invoice","Payment","Applied",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.length===0&&<tr><td colSpan={10} style={{ textAlign:"center", color:"var(--text3)", padding:40 }}>No cameras found.</td></tr>}
              {filtered.map(c=>{
                const d=daysLeft(c.expiry_date);
                const invDue=d>=0&&d<=60&&!c.inv_sent, awaitPmt=c.inv_sent&&!c.pmt_received, readyApply=c.pmt_received&&!c.was_applied;
                const bg=readyApply?"rgba(139,92,246,0.05)":awaitPmt?"rgba(234,179,8,0.04)":invDue?"rgba(59,130,246,0.04)":"";
                return (
                  <tr key={c.id} style={{ background:bg }}>
                    <td><strong>{c.propName}</strong></td>
                    <td><strong>{c.name}</strong>{c.notes&&<div style={{ fontSize:10, color:"var(--text3)" }}>{c.notes}</div>}</td>
                    <td className="mono">{c.mac_address||"—"}</td>
                    <td className="mono">{c.license_key||"—"}</td>
                    <td className="mono">{c.expiry_date||"—"}</td>
                    <td><StatusBadge expiry={c.expiry_date} /></td>
                    <td>{c.inv_sent?<Badge label={c.inv_number||"Sent"} color="green"/>:invDue?<Badge label="Due" color="blue"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>{c.pmt_received?<Badge label="Paid" color="green"/>:awaitPmt?<Badge label="Awaiting" color="warning"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>{c.was_applied?<Badge label="Applied" color="green"/>:readyApply?<Badge label="Ready" color="purple"/>:<span style={{ color:"var(--text3)" }}>—</span>}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding:"4px 9px",fontSize:11,marginRight:4 }} onClick={()=>onEditCamera(c,c.propId)}>Edit</button>
                      <button className="btn btn-danger" style={{ padding:"4px 9px",fontSize:11 }} onClick={()=>onDeleteCamera(c.id,c.propId)}>Del</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Invoices ──────────────────────────────────────────────────
function Invoices({ properties, onMarkPaid, onCreateInvoice }) {
  injectCSS();
  const allCams = properties.flatMap(p=>(p.cameras||[]).map(c=>({...c,propName:p.name,propId:p.id,propContact:p.contact_name,propEmail:p.contact_email,propTerm:p.billing_term||1})));
  const due = allCams.filter(c=>{ const d=daysLeft(c.expiry_date); return d>=0&&d<=60; }).sort((a,b)=>daysLeft(a.expiry_date)-daysLeft(b.expiry_date));
  const pending  = due.filter(c=>!c.inv_sent);
  const awaiting = due.filter(c=>c.inv_sent&&!c.pmt_received);
  const paid     = due.filter(c=>c.pmt_received);
  const pendingGroups = {};
  pending.forEach(c=>{ if(!pendingGroups[c.propId]) pendingGroups[c.propId]={name:c.propName,contact:c.propContact,email:c.propEmail,term:c.propTerm,cams:[]}; pendingGroups[c.propId].cams.push(c); });
  const exportCSV = () => {
    const keys=["propName","name","license_key","expiry_date","inv_number","inv_date","inv_years","inv_amount","pmt_date","pmt_amount"];
    const labels=["Property","Camera","License Key","Expiry","Inv#","Inv Date","Inv Yrs","Inv Amt","Pmt Date","Pmt Amt"];
    const csv=labels.join(",")+"\n"+due.map(c=>keys.map(k=>`"${(c[k]||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="vortex-invoices.csv"; a.click();
  };
  return (
    <div className="fade-in" style={{ padding:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800 }}>Invoice & Payment Tracking</h2>
          <p style={{ fontSize:12, color:"var(--text3)", marginTop:2 }}>Licenses expiring within 60 days</p>
        </div>
        <button className="btn btn-success" onClick={exportCSV}>⬇ Export CSV</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:22 }}>
        {[["Invoice Pending",pending.length,"var(--yellow)"],["Awaiting Pmt",awaiting.length,"var(--blue)"],["Paid",paid.length,"var(--green)"],["Total Period",due.length,"var(--text)"]].map(([l,v,c])=>(
          <div key={l} className="stat-card" style={{ cursor:"default" }}>
            <div className="val" style={{ color:c }}>{v}</div>
            <div className="lbl">{l}</div>
          </div>
        ))}
      </div>

      {Object.keys(pendingGroups).length>0&&(
        <>
          <div className="section-title">Invoice Not Yet Sent ({pending.length} cameras)</div>
          {Object.entries(pendingGroups).map(([pid,g])=>{
            const amt=g.cams.length*PRICE*g.term;
            return (
              <div key={pid} className="card" style={{ borderLeft:"3px solid var(--yellow)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:14 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700 }}>{g.name}</div>
                    <div style={{ fontSize:11, color:"var(--text3)", marginTop:2 }}>{g.contact}{g.email&&` · ${g.email}`}</div>
                    <div style={{ fontSize:12, color:"var(--text2)", marginTop:4 }}>{g.cams.length} cams × {money(PRICE)} × {g.term}yr = <strong style={{ color:"var(--green)", fontFamily:"'JetBrains Mono',monospace" }}>{money(amt)}</strong></div>
                  </div>
                  <button className="btn btn-primary" onClick={()=>onCreateInvoice(pid)}>Create Invoice</button>
                </div>
                <table className="tbl">
                  <thead><tr>{["Camera","MAC","License Key","Expiry","Days Left"].map(h=><th key={h}>{h}</th>)}</tr></thead>
                  <tbody>{g.cams.map(c=>(
                    <tr key={c.id} style={{ background:"rgba(59,130,246,0.04)" }}>
                      <td><strong>{c.name}</strong></td>
                      <td className="mono">{c.mac_address||"—"}</td>
                      <td className="mono">{c.license_key||"—"}</td>
                      <td className="mono">{c.expiry_date}</td>
                      <td><StatusBadge expiry={c.expiry_date} /></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            );
          })}
        </>
      )}

      {awaiting.length>0&&(
        <>
          <div className="section-title">Invoice Sent — Awaiting Payment ({awaiting.length})</div>
          <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden", marginBottom:20 }}>
            <div style={{ overflowX:"auto" }}>
              <table className="tbl">
                <thead><tr>{["Property","Camera","License Key","Invoice #","Date","Amount","Term","Days",""].map(h=><th key={h}>{h}</th>)}</tr></thead>
                <tbody>{awaiting.map(c=>(
                  <tr key={c.id} style={{ background:"rgba(234,179,8,0.03)" }}>
                    <td><strong>{c.propName}</strong></td>
                    <td>{c.name}</td>
                    <td className="mono">{c.license_key||"—"}</td>
                    <td style={{ color:"var(--blue)", fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{c.inv_number||"—"}</td>
                    <td style={{ fontSize:11 }}>{c.inv_date||"—"}</td>
                    <td style={{ color:"var(--green)", fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{money(c.inv_amount)}</td>
                    <td><Badge label={c.inv_years+"-yr"} color={c.inv_years===1?"warning":"purple"} /></td>
                    <td><StatusBadge expiry={c.expiry_date} /></td>
                    <td><button className="btn btn-success" style={{ padding:"4px 11px",fontSize:11 }} onClick={()=>onMarkPaid(c)}>Record Payment</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {paid.length>0&&(
        <>
          <div className="section-title">Paid ({paid.length})</div>
          <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table className="tbl">
                <thead><tr>{["Property","Camera","License Key","Invoice #","Inv Amt","Pmt Date","Pmt Amt","Applied?"].map(h=><th key={h}>{h}</th>)}</tr></thead>
                <tbody>{paid.map(c=>(
                  <tr key={c.id} style={{ background:"rgba(34,197,94,0.03)" }}>
                    <td><strong>{c.propName}</strong></td>
                    <td>{c.name}</td>
                    <td className="mono">{c.license_key||"—"}</td>
                    <td style={{ color:"var(--blue)", fontFamily:"'JetBrains Mono',monospace" }}>{c.inv_number||"—"}</td>
                    <td style={{ color:"var(--green)", fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{money(c.inv_amount)}</td>
                    <td style={{ fontSize:11 }}>{c.pmt_date||"—"}</td>
                    <td style={{ color:"var(--green)", fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{money(c.pmt_amount)}</td>
                    <td>{c.was_applied?<Badge label="✓ Applied" color="green"/>:<Badge label="Pending" color="critical"/>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {due.length===0&&<div style={{ textAlign:"center", padding:60, color:"var(--text3)" }}><div style={{ fontSize:40, marginBottom:10 }}>✅</div><div style={{ fontWeight:600 }}>All clear! No licenses expiring within 60 days.</div></div>}
    </div>
  );
}

// ── Apply Licenses ────────────────────────────────────────────
function ApplyLicenses({ properties, onMarkApplied }) {
  injectCSS();
  const allCams = properties.flatMap(p=>(p.cameras||[]).map(c=>({...c,propName:p.name,propId:p.id})));
  const ready = allCams.filter(c=>c.pmt_received&&!c.was_applied).sort((a,b)=>daysLeft(a.expiry_date)-daysLeft(b.expiry_date));
  const done  = allCams.filter(c=>c.was_applied).slice(0,15);
  const groups = {};
  ready.forEach(c=>{ if(!groups[c.propId]) groups[c.propId]={name:c.propName,cams:[]}; groups[c.propId].cams.push(c); });

  return (
    <div className="fade-in" style={{ padding:24 }}>
      <h2 style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>Apply Licenses in Vortex</h2>
      <p style={{ fontSize:12, color:"var(--text3)", marginBottom:20 }}>Payment confirmed — apply in Vortex VMS portal then mark as applied below</p>

      {ready.length===0 ? (
        <div className="card" style={{ textAlign:"center", padding:60 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:18, fontWeight:800, color:"var(--green)", marginBottom:6 }}>All Caught Up!</div>
          <div style={{ fontSize:13, color:"var(--text3)" }}>No licenses pending application</div>
        </div>
      ) : (
        <>
          <div style={{ background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.2)", borderRadius:9, padding:"11px 15px", marginBottom:16, fontSize:12, color:"var(--blue)", display:"flex", gap:8, alignItems:"flex-start" }}>
            <span>ℹ</span>
            <span>Apply each license key in the Vortex VMS portal, then check the box below. The expiry date will automatically extend by the billing term.</span>
          </div>
          {Object.entries(groups).map(([pid,g])=>(
            <div key={pid} className="card" style={{ borderLeft:"3px solid var(--purple)", marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                <div style={{ fontSize:14, fontWeight:700 }}>{g.name}</div>
                <button className="btn btn-purple" onClick={()=>onMarkApplied(g.cams.map(c=>c.id),pid)}>✓ Mark All Applied</button>
              </div>
              {g.cams.map(c=>(
                <div key={c.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", background:"var(--bg3)", borderRadius:9, marginBottom:8, border:"1px solid var(--border)" }}>
                  <input type="checkbox" onChange={e=>e.target.checked&&onMarkApplied([c.id],pid)} style={{ width:17, height:17, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>{c.name}</div>
                    <div style={{ fontSize:11, color:"var(--text3)", display:"flex", gap:14, flexWrap:"wrap" }}>
                      <span>MAC: <strong className="mono" style={{ color:"var(--text2)" }}>{c.mac_address||"—"}</strong></span>
                      <span>Serial: <strong className="mono" style={{ color:"var(--text2)" }}>{c.serial_number||"—"}</strong></span>
                      <span>Key: <strong className="mono" style={{ color:"var(--text2)" }}>{c.license_key||"—"}</strong></span>
                    </div>
                    <div style={{ fontSize:11, color:"var(--text3)", marginTop:3, display:"flex", gap:14, flexWrap:"wrap" }}>
                      <span>Expiry: {c.expiry_date}</span>
                      <span>Invoice: {c.inv_number||"—"}</span>
                      <span>Paid: {c.pmt_date||"—"}</span>
                    </div>
                  </div>
                  <Badge label={c.inv_years+"-yr"} color="purple" />
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {done.length>0&&(
        <>
          <div className="section-title">Recently Applied</div>
          <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
            <table className="tbl">
              <thead><tr>{["Property","Camera","License Key","MAC Address","Applied Date","New Expiry"].map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>{done.map(c=>(
                <tr key={c.id} style={{ background:"rgba(34,197,94,0.03)" }}>
                  <td><strong>{c.propName}</strong></td>
                  <td>{c.name}</td>
                  <td className="mono">{c.license_key||"—"}</td>
                  <td className="mono">{c.mac_address||"—"}</td>
                  <td style={{ fontSize:11 }}>{c.applied_date||"—"}</td>
                  <td style={{ fontWeight:700, color:"var(--green)", fontFamily:"'JetBrains Mono',monospace" }}>{c.expiry_date||"—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Payment Modal ─────────────────────────────────────────────
function PaymentModal({ camera, onSave, onClose }) {
  injectCSS();
  const [form, setForm] = useState({ pmt_date:new Date().toISOString().split("T")[0], pmt_amount:camera.inv_amount||0, pmt_method:"Check" });
  return (
    <div className="modal-bg">
      <div className="modal">
        <div style={{ fontSize:16, fontWeight:800, marginBottom:14 }}>Record Payment</div>
        <div style={{ background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.2)", borderRadius:8, padding:"10px 13px", marginBottom:16, fontSize:12, color:"var(--blue)" }}>
          <strong>{camera.propName}</strong> — {camera.name}<br/>
          Invoice: <span className="mono">{camera.inv_number||"—"}</span> · Amount due: <strong className="mono">{money(camera.inv_amount)}</strong>
        </div>
        <div style={{ marginBottom:12 }}><label className="lbl">Payment Date *</label><input className="inp" type="date" value={form.pmt_date} onChange={e=>setForm(f=>({...f,pmt_date:e.target.value}))} /></div>
        <div style={{ marginBottom:12 }}><label className="lbl">Payment Amount *</label><input className="inp" type="number" value={form.pmt_amount} onChange={e=>setForm(f=>({...f,pmt_amount:parseFloat(e.target.value)||0}))} /></div>
        <div style={{ marginBottom:20 }}><label className="lbl">Payment Method</label>
          <select className="inp" value={form.pmt_method} onChange={e=>setForm(f=>({...f,pmt_method:e.target.value}))}>
            {["Check","ACH / Bank Transfer","Credit Card","Wire Transfer","Other"].map(m=><option key={m}>{m}</option>)}
          </select>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-orange" onClick={()=>onSave(form)}>Save Payment</button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  injectCSS();
  const [currentUser, setCurrentUser] = useState(()=>{ const u=localStorage.getItem("user"); return u?JSON.parse(u):null; });
  const [token, setToken] = useState(()=>localStorage.getItem("token")||null);
  const [isDemo, setIsDemo] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [selectedPropId, setSelectedPropId] = useState(null);
  const [editCamera, setEditCamera] = useState(null);
  const [editProperty, setEditProperty] = useState(null);
  const [addCameraPropId, setAddCameraPropId] = useState(null);
  const [paymentCamera, setPaymentCamera] = useState(null);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleLogin = (user,tok) => { setCurrentUser(user); setToken(tok); };
  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setCurrentUser(null); setToken(null); setIsDemo(false); setProperties([]); setPage("dashboard"); };
  const handleDemo = () => { setCurrentUser(DEMO_USER); setToken("demo"); setIsDemo(true); setProperties(JSON.parse(JSON.stringify(DEMO_PROPERTIES))); };

  const H = useCallback(()=>({ "Content-Type":"application/json","Accept":"application/json","Authorization":`Bearer ${token}` }),[token]);

  const fetchProperties = useCallback(async()=>{
    if(isDemo) return;
    try { const r=await fetch(`${API_URL}/properties`,{headers:H()}); const d=await r.json(); if(r.ok) setProperties(d); }
    catch(e){console.error(e);}
  },[H,isDemo]);

  useEffect(()=>{ if(token&&!isDemo) fetchProperties(); },[token]);

  const isAdmin = currentUser?.role==="admin";
  if(!currentUser||!token) return <LoginPage onLogin={handleLogin} onDemo={handleDemo} />;

  const navigate = (p,propId=null) => { setPage(p); if(propId) setSelectedPropId(propId); setEditCamera(null); setEditProperty(null); setAddCameraPropId(null); };

  const saveProperty = async (form) => {
    if(!form.name){alert("Property name required.");return;}
    if(isDemo){ if(editProperty) setProperties(p=>p.map(x=>x.id===editProperty.id?{...x,...form}:x)); else setProperties(p=>[...p,{id:Date.now(),...form,cameras:[]}]); navigate("properties"); return; }
    setSaving(true);
    try { const isEdit=!!editProperty; const r=await fetch(`${API_URL}/properties${isEdit?"/"+editProperty.id:""}`,{method:isEdit?"PUT":"POST",headers:H(),body:JSON.stringify(form)}); if(r.ok){await fetchProperties();navigate("properties");} }
    catch(e){console.error(e);}
    setSaving(false);
  };

  const deleteProperty = async (id) => {
    if(!window.confirm("Delete this property and ALL its cameras?")) return;
    if(isDemo){setProperties(p=>p.filter(x=>x.id!==id));navigate("properties");return;}
    try{await fetch(`${API_URL}/properties/${id}`,{method:"DELETE",headers:H()});await fetchProperties();navigate("properties");}catch(e){console.error(e);}
  };

  const saveCamera = async (form) => {
    if(!form.name||!form.license_key||!form.expiry_date){alert("Camera name, license key and expiry required.");return;}
    if(isDemo){
      const pid=parseInt(form.property_id);
      if(editCamera) setProperties(p=>p.map(prop=>({...prop,cameras:prop.cameras.map(c=>c.id===editCamera.id?{...c,...form,property_id:pid}:c)})));
      else setProperties(p=>p.map(prop=>prop.id===pid?{...prop,cameras:[...prop.cameras,{id:Date.now(),...form,property_id:pid}]}:prop));
      navigate(selectedPropId?"property":"cameras",selectedPropId); return;
    }
    setSaving(true);
    try { const isEdit=!!editCamera; const r=await fetch(`${API_URL}/cameras${isEdit?"/"+editCamera.id:""}`,{method:isEdit?"PUT":"POST",headers:H(),body:JSON.stringify(form)}); if(r.ok){await fetchProperties();navigate(selectedPropId?"property":"cameras",selectedPropId);} }
    catch(e){console.error(e);}
    setSaving(false);
  };

  const deleteCamera = async (camId,propId) => {
    if(!window.confirm("Delete this camera?")) return;
    if(isDemo){setProperties(p=>p.map(prop=>({...prop,cameras:prop.cameras.filter(c=>c.id!==camId)})));return;}
    try{await fetch(`${API_URL}/cameras/${camId}`,{method:"DELETE",headers:H()});await fetchProperties();}catch(e){console.error(e);}
  };

  const createInvoice = async (propId) => {
    const prop=properties.find(p=>p.id===propId); if(!prop) return;
    const invNum="INV-"+Date.now().toString().slice(-5), today=new Date().toISOString().split("T")[0], t=prop.billing_term||1;
    if(isDemo){
      setProperties(p=>p.map(prop2=>prop2.id===propId?{...prop2,cameras:prop2.cameras.map(c=>{const d=daysLeft(c.expiry_date);if(d<0||d>60||c.inv_sent)return c;return{...c,inv_sent:true,inv_date:today,inv_years:t,inv_amount:PRICE*t,inv_number:invNum};})}:prop2));
      alert(`Invoice ${invNum} created!\n\n${prop.name}\n${prop.cameras.length} cameras · ${t}-year · Total: $${prop.cameras.length*PRICE*t}`); return;
    }
    try{await fetch(`${API_URL}/properties/${propId}/invoice`,{method:"POST",headers:H(),body:JSON.stringify({inv_number:invNum,inv_date:today,inv_years:t,inv_amount:PRICE*t})});await fetchProperties();alert(`Invoice ${invNum} created!`);}catch(e){console.error(e);}
  };

  const savePayment = async (form) => {
    if(!paymentCamera) return;
    if(isDemo){setProperties(p=>p.map(prop=>({...prop,cameras:prop.cameras.map(c=>c.id===paymentCamera.id?{...c,pmt_date:form.pmt_date,pmt_amount:form.pmt_amount,pmt_received:true}:c)})));setPaymentCamera(null);return;}
    try{await fetch(`${API_URL}/cameras/${paymentCamera.id}`,{method:"PUT",headers:H(),body:JSON.stringify({...paymentCamera,pmt_date:form.pmt_date,pmt_amount:form.pmt_amount,pmt_received:true})});await fetchProperties();setPaymentCamera(null);}catch(e){console.error(e);}
  };

  const markApplied = async (camIds,propId) => {
    const today=new Date().toISOString().split("T")[0]; const prop=properties.find(p=>p.id===propId);
    if(isDemo){setProperties(p=>p.map(prop2=>prop2.id===propId?{...prop2,cameras:prop2.cameras.map(c=>{if(!camIds.includes(c.id))return c;const yrs=c.inv_years||1;const nd=new Date(c.expiry_date);nd.setFullYear(nd.getFullYear()+yrs);return{...c,was_applied:true,applied_date:today,expiry_date:nd.toISOString().split("T")[0]};})}:prop2));return;}
    for(const camId of camIds){const cam=prop?.cameras.find(c=>c.id===camId);if(!cam)continue;const yrs=cam.inv_years||1;const nd=new Date(cam.expiry_date);nd.setFullYear(nd.getFullYear()+yrs);try{await fetch(`${API_URL}/cameras/${camId}`,{method:"PUT",headers:H(),body:JSON.stringify({...cam,was_applied:true,applied_date:today,expiry_date:nd.toISOString().split("T")[0]})});}catch(e){console.error(e);}}
    await fetchProperties();
  };

  const selectedProp = properties.find(p=>p.id===selectedPropId);
  const TABS = [
    {k:"dashboard",l:"Dashboard"},{k:"properties",l:"Properties"},{k:"cameras",l:"All Cameras"},{k:"invoices",l:"Invoices"},{k:"apply",l:"Apply Licenses"},
    ...(isAdmin?[{k:"users",l:"Users"}]:[]),
  ];
  const activeTab = ["addproperty","editproperty","property"].includes(page)?"properties":["addcamera","editcamera"].includes(page)?"cameras":page;

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,var(--bg3),var(--bg2))", borderBottom:"1px solid var(--border)", padding:"12px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:38, height:38, background:"linear-gradient(135deg,var(--orange),#ea580c)", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>⚡</div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, letterSpacing:"-0.2px" }}>VORTEX LICENSE TRACKER PRO</div>
            <div style={{ fontSize:10, color:"var(--text3)", textTransform:"uppercase", letterSpacing:"0.8px" }}>Vivotek / Vortex VMS — License Management</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          
          {saving && <span style={{ fontSize:11, color:"var(--text3)" }}>💾 Saving...</span>}
          <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--bg4)", border:"1px solid var(--border)", borderRadius:8, padding:"6px 12px" }}>
            <div style={{ width:26, height:26, background:"linear-gradient(135deg,var(--orange),#ea580c)", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>
              {isAdmin?"👑":"🔍"}
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:700 }}>{currentUser.name}</div>
              <div style={{ fontSize:10, color:"var(--text3)", textTransform:"capitalize" }}>{currentUser.role}</div>
            </div>
          </div>
          <button className="btn btn-danger" style={{ padding:"6px 12px", fontSize:11 }} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      {/* Nav */}
      <div style={{ background:"var(--bg3)", borderBottom:"1px solid var(--border)", display:"flex", overflowX:"auto", paddingLeft:8 }}>
        {TABS.map(t=>(
          <button key={t.k} className={`nav-btn${activeTab===t.k?" active":""}`} onClick={()=>navigate(t.k)}>{t.l}</button>
        ))}
      </div>

      {/* Pages */}
      {page==="dashboard"   && <Dashboard properties={properties} onNavigate={(p,id)=>{setSelectedPropId(id||null);navigate(p,id);}} />}
      {page==="properties"  && (
        <div className="fade-in" style={{ padding:24 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <h2 style={{ fontSize:20, fontWeight:800 }}>Properties <span style={{ color:"var(--text3)", fontWeight:400, fontSize:14 }}>({properties.length})</span></h2>
            <button className="btn btn-orange" onClick={()=>{setEditProperty(null);navigate("addproperty");}}>+ Add Property</button>
          </div>
          {properties.map(p=>{
            const pc=p.cameras||[];
            const invDue=pc.filter(c=>{const d=daysLeft(c.expiry_date);return d>=0&&d<=60&&!c.inv_sent;}).length;
            const awaitPmt=pc.filter(c=>c.inv_sent&&!c.pmt_received).length;
            const readyApp=pc.filter(c=>c.pmt_received&&!c.was_applied).length;
            const sts=pc.map(c=>getStatus(daysLeft(c.expiry_date)));
            const worst=sts.includes("expired")?"expired":sts.includes("critical")?"critical":sts.includes("warning")?"warning":"active";
            const stripeColor={active:"var(--green)",warning:"var(--yellow)",critical:"var(--red)",expired:"var(--text3)"}[worst];
            return (
              <div key={p.id} className="card" style={{ borderLeft:"3px solid "+stripeColor, display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:14 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:"var(--text3)", marginBottom:6 }}>
                    {p.address&&<span style={{ marginRight:14 }}>📍 {p.address}</span>}
                    {p.contact_name&&<span style={{ marginRight:14 }}>👤 {p.contact_name}</span>}
                    {p.contact_email&&<span>✉ {p.contact_email}</span>}
                  </div>
                  <div style={{ fontSize:12, color:"var(--text2)", marginBottom:8 }}>
                    {p.billing_term||1}-year · {pc.length} cameras · <span style={{ color:"var(--green)", fontFamily:"'JetBrains Mono',monospace" }}>{money(pc.length*PRICE)}/yr</span>
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {readyApp>0&&<Badge label={readyApp+" to apply"} color="purple"/>}
                    {awaitPmt>0&&<Badge label={awaitPmt+" unpaid"} color="warning"/>}
                    {invDue>0&&<Badge label={invDue+" invoice due"} color="blue"/>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  <button className="btn btn-primary" style={{ fontSize:11 }} onClick={()=>{setSelectedPropId(p.id);navigate("property",p.id);}}>View Cameras</button>
                  <button className="btn btn-ghost" style={{ fontSize:11 }} onClick={()=>{setEditProperty(p);navigate("editproperty");}}>Edit</button>
                  <button className="btn btn-danger" style={{ fontSize:11 }} onClick={()=>deleteProperty(p.id)}>Delete</button>
                </div>
              </div>
            );
          })}
          {properties.length===0&&<div style={{ textAlign:"center", padding:60, color:"var(--text3)" }}>No properties yet.</div>}
        </div>
      )}
      {(page==="addproperty"||page==="editproperty") && <PropertyForm property={editProperty} onSave={saveProperty} onCancel={()=>navigate("properties")} />}
      {page==="property" && selectedProp && (
        <PropertyDetail property={selectedProp} onBack={()=>navigate("properties")}
          onEditProp={()=>{setEditProperty(selectedProp);navigate("editproperty");}}
          onAddCamera={()=>{setEditCamera(null);setAddCameraPropId(selectedPropId);navigate("addcamera");}}
          onEditCamera={c=>{setEditCamera(c);navigate("editcamera");}}
          onDeleteCamera={camId=>deleteCamera(camId,selectedPropId)}
          onDeleteProp={()=>deleteProperty(selectedPropId)}
        />
      )}
      {(page==="addcamera"||page==="editcamera") && (
        <CameraForm camera={editCamera} properties={properties} defaultPropId={addCameraPropId||selectedPropId} onSave={saveCamera} onCancel={()=>navigate(selectedPropId?"property":"cameras",selectedPropId)} />
      )}
      {page==="cameras"   && <AllCameras properties={properties} onAddCamera={()=>{setEditCamera(null);setAddCameraPropId(null);navigate("addcamera");}} onEditCamera={(c,propId)=>{setEditCamera(c);setSelectedPropId(propId);navigate("editcamera");}} onDeleteCamera={(camId,propId)=>deleteCamera(camId,propId)} />}
      {page==="invoices"  && <Invoices properties={properties} onMarkPaid={c=>{const prop=properties.find(p=>p.id===c.propId);setPaymentCamera({...c,propName:prop?.name});}} onCreateInvoice={createInvoice} />}
      {page==="apply"     && <ApplyLicenses properties={properties} onMarkApplied={markApplied} />}
      {page==="users"     && isAdmin && <UsersPage token={token} />}

      {paymentCamera && <PaymentModal camera={paymentCamera} onSave={savePayment} onClose={()=>setPaymentCamera(null)} />}
    </div>
  );
}
